import {
  ChangeEvent,
  FocusEvent,
  ForwardedRef,
  MouseEvent,
  ReactElement,
  RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';

import Button from '@/common/Button';
import { Dayts } from '@/utils';
import Group from '@/common/Group';
import { TIME_FORMAT } from '@/constants';
import TimeInputItem from '@/common/TimeInput/TimeInputItem';
import TimeInputList from '@/common/TimeInput/TimeInputList';
import { formatTime } from '@/common/TimeInput/utils';
import useForwardRef from '@/hooks/useForwardRef';

export type TimeInputPopoverProps = {
  value: string;
  format: string;
  showHours?: boolean;
  showMinutes?: boolean;
  showSeconds?: boolean;
  disableHours?: boolean;
  disableMinutes?: boolean;
  disableSeconds?: boolean;
  target: RefObject<HTMLInputElement>;
  onChange: (
    value: string,
    focus?: boolean,
    event?: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>
  ) => string;
  display?: boolean;
  maxHeight?: number;
};

const TimeInputPopover = forwardRef(
  (
    {
      value,
      format = TIME_FORMAT,
      showHours,
      showMinutes,
      showSeconds,
      disableHours,
      disableMinutes,
      disableSeconds,
      target,
      onChange,
      display,
      maxHeight,
    }: TimeInputPopoverProps,
    ref: ForwardedRef<HTMLDivElement>
  ): ReactElement => {
    const scrollRef = useForwardRef<HTMLDivElement>(ref);

    const handleChange =
      (type: 'hours' | 'minutes' | 'seconds', val: number): (() => void) =>
      (): void => {
        let currentTime = new Dayts().setTime(value || '00:00:00');
        if (type === 'hours') {
          currentTime = currentTime.set('hours', val);
        } else if (type === 'minutes') {
          currentTime = currentTime.set('minutes', val);
        } else if (type === 'seconds') {
          currentTime = currentTime.set('seconds', val);
        }
        const newValue = currentTime.format(format);
        onChange(newValue, true);
      };
    const getValues = useCallback(
      (val: string): number[] => {
        val = formatTime(format)(val);
        const time = new Dayts().setTime(`${val}`);
        return [parseInt(time.hour()) || 0, parseInt(time.minute()) || 0, parseInt(time.second()) || 0];
      },
      [format]
    );
    const scrollToSelected = useCallback(
      (value?: string): NodeJS.Timeout => {
        const [hours, minutes, seconds] = getValues(value || '');
        const timeout = setTimeout(() => {
          scrollRef.current?.querySelector?.(`.TimeInput-Hour-${hours}`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
          scrollRef.current?.querySelector?.(`.TimeInput-Minute-${minutes}`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
          scrollRef.current?.querySelector?.(`.TimeInput-Second-${seconds}`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        }, 100);
        return timeout;
      },
      [getValues, scrollRef]
    );

    const [hours, minutes, seconds] = useMemo((): number[] => getValues(value), [getValues, value]);

    useEffect((): (() => void) | void => {
      const timeout = scrollToSelected(value);
      return (): void => clearTimeout(timeout);
    }, [scrollToSelected, value]);

    const Content = (
      <div className="TimeInput-Interface" style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined }} ref={scrollRef}>
        {showHours !== undefined && showHours !== false && (
          <TimeInputList>
            {Array.from(
              { length: 24 },
              (_: unknown, i: number): ReactElement => (
                <TimeInputItem
                  name="Hour"
                  unit={i}
                  selected={hours === i}
                  onClick={handleChange('hours', i)}
                  disabled={disableHours}
                  key={i}
                />
              )
            )}
          </TimeInputList>
        )}
        {showMinutes !== undefined && showMinutes !== false && (
          <TimeInputList>
            {Array.from(
              { length: 60 },
              (_: unknown, i: number): ReactElement => (
                <TimeInputItem
                  name="Minute"
                  unit={i}
                  selected={minutes === i}
                  onClick={handleChange('minutes', i)}
                  disabled={disableMinutes}
                  key={i}
                />
              )
            )}
          </TimeInputList>
        )}
        {showSeconds !== undefined && showSeconds !== false && (
          <TimeInputList>
            {Array.from(
              { length: 60 },
              (_: undefined, i: number): ReactElement => (
                <TimeInputItem
                  name="Second"
                  unit={i}
                  selected={seconds === i}
                  onClick={handleChange('seconds', i)}
                  disabled={disableSeconds}
                  key={i}
                />
              )
            )}
          </TimeInputList>
        )}
      </div>
    );

    if (display !== undefined && display !== false) return Content;
    return (
      <>
        {Content}
        <Group className="TimeInput-Controls">
          <Button
            variant="outline-primary"
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              const now = formatTime(format)(new Dayts().format(format));
              onChange(now, true);
              event.currentTarget.blur();
            }}
            tabIndex={-1}
          >
            Now
          </Button>
          <Button
            variant="outline-primary"
            onClick={(event: MouseEvent<HTMLButtonElement>): void => {
              target.current?.blur();
              event.currentTarget.blur();
            }}
            tabIndex={-1}
          >
            Ok
          </Button>
        </Group>
      </>
    );
  }
);
TimeInputPopover.displayName = 'TimeInputPopover';

export default TimeInputPopover;
